import { useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// Progressive-enhancement scroll motion, scoped to a page root ref.
// CSS hides [data-reveal]/[data-img]/.scrub words only while `.motion` is set,
// so without JS (or with reduced-motion) everything renders fully visible.
export default function useReveal(scopeRef) {
  useLayoutEffect(() => {
    const root = scopeRef.current;
    if (!root) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    document.documentElement.classList.add('motion');

    const ctx = gsap.context(() => {
      root.querySelectorAll('[data-reveal]').forEach((node) => {
        gsap.to(node, {
          opacity: 1,
          y: 0,
          duration: 0.9,
          ease: 'power3.out',
          scrollTrigger: { trigger: node, start: 'top 86%', once: true },
        });
      });

      // Image Scale & Fade on scroll
      root.querySelectorAll('[data-img]').forEach((node) => {
        gsap.to(node, {
          opacity: 1,
          scale: 1,
          duration: 1.1,
          ease: 'power2.out',
          scrollTrigger: { trigger: node, start: 'top 90%', once: true },
        });
      });

      // Scrubbing word reveal
      root.querySelectorAll('.scrub').forEach((container) => {
        gsap.to(container.querySelectorAll('.word'), {
          opacity: 1,
          ease: 'none',
          stagger: 0.06,
          scrollTrigger: { trigger: container, start: 'top 78%', end: 'bottom 52%', scrub: true },
        });
      });
    }, root);

    const refresh = () => ScrollTrigger.refresh();
    window.addEventListener('load', refresh);
    const id = setTimeout(refresh, 400);

    return () => {
      clearTimeout(id);
      window.removeEventListener('load', refresh);
      ctx.revert();
      document.documentElement.classList.remove('motion');
    };
  }, [scopeRef]);
}

// Split a string into <span class="word"> tokens for the scrub reveal.
export function Scrubbed({ text }) {
  return text.split(' ').map((w, i) => (
    <span className="word" key={i}>
      {w}
      {i < text.split(' ').length - 1 ? ' ' : ''}
    </span>
  ));
}
